/**
 * CrewAssignmentCard — read-only-ish card representing one Crew Assignment.
 * Rendered on:
 *   • Supervisor dashboard (assignments they created)
 *   • Crew Leader dashboard (assignments dispatched to them + reorder/dispatch UI)
 *   • Agent dashboard (their portion of assignments they're on)
 */
import { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { ChevronUp, ChevronDown, Building2, MapPin, Users, HardHat, ClipboardList } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  dispatched:   'bg-blue-500/15 text-blue-300 border-blue-500/40',
  in_progress:  'bg-amber-500/15 text-amber-300 border-amber-500/40',
  completed:    'bg-emerald-500/15 text-emerald-300 border-emerald-500/40',
  cancelled:    'bg-zinc-500/15 text-zinc-400 border-zinc-500/40',
};

export default function CrewAssignmentCard({ assignment, viewerRole, viewerUserId, onChanged }) {
  const [localOrder, setLocalOrder] = useState(assignment.properties.map((p) => p.property_id));
  const [dispatch, setDispatch] = useState(() => {
    const d = {};
    assignment.properties.forEach((p) => {
      d[p.property_id] = p.assigned_agent_ids || [];
    });
    return d;
  });
  const [saving, setSaving] = useState(false);

  const isCrewLeader = assignment.crew_leader?.user_id === viewerUserId;
  const isAgent = ['agent', 'site_agent', 'staff', 'driver'].includes(viewerRole);
  const canOrganize = isCrewLeader || ['admin', 'company_manager'].includes(viewerRole);

  // Agents only see the properties they're dispatched to.
  const visibleProps = isAgent
    ? assignment.properties.filter((p) => (p.assigned_agent_ids || []).includes(viewerUserId))
    : localOrder
        .map((pid) => assignment.properties.find((p) => p.property_id === pid))
        .filter(Boolean);

  const moveProp = (pid, dir) => {
    setLocalOrder((prev) => {
      const idx = prev.indexOf(pid);
      if (idx < 0) return prev;
      const next = [...prev];
      const swap = idx + dir;
      if (swap < 0 || swap >= next.length) return prev;
      [next[idx], next[swap]] = [next[swap], next[idx]];
      return next;
    });
  };

  const toggleAgentOnProp = (pid, aid) => {
    setDispatch((prev) => {
      const cur = prev[pid] || [];
      const next = cur.includes(aid) ? cur.filter((x) => x !== aid) : [...cur, aid];
      return { ...prev, [pid]: next };
    });
  };

  const save = async (nextStatus) => {
    setSaving(true);
    try {
      const body = { property_order: localOrder, dispatch };
      if (nextStatus) body.status = nextStatus;
      const res = await fetch(
        `${API}/api/ops/crew-assignments/${assignment.assignment_id}/organize`,
        {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.detail || 'Failed to save changes');
        return;
      }
      toast.success('Assignment updated — agents notified.');
      onChanged?.(data.assignment);
    } catch (_) {
      toast.error('Network error');
    } finally {
      setSaving(false);
    }
  };

  const statusKey = assignment.status || 'dispatched';
  const statusStyle = STATUS_STYLES[statusKey] || STATUS_STYLES.dispatched;

  return (
    <Card
      className="p-4 border"
      style={{ backgroundColor: '#0b0b0b', borderColor: 'rgba(201,169,98,0.35)' }}
      data-testid={`crew-assignment-card-${assignment.assignment_id}`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <ClipboardList className="h-4 w-4 text-[#C9A962]" />
            <span
              className="text-xs font-bold uppercase tracking-widest"
              style={{ color: '#C9A962', fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Crew Assignment · {assignment.assignment_id.slice(0, 14)}
            </span>
            <Badge className={`text-[10px] font-bold uppercase border ${statusStyle}`}>{statusKey.replace('_', ' ')}</Badge>
          </div>
          <div className="mt-1 text-[11px] text-zinc-400 flex flex-wrap items-center gap-3">
            <span className="flex items-center gap-1"><HardHat className="h-3 w-3" />{assignment.crew_leader?.name || '—'}</span>
            <span className="flex items-center gap-1"><Users className="h-3 w-3" />{assignment.agents.length} agents</span>
            <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{assignment.cities.length} cities</span>
            <span className="flex items-center gap-1"><Building2 className="h-3 w-3" />{assignment.properties.length} props · {assignment.total_units} units</span>
          </div>
          {assignment.cities.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1" data-testid={`crew-assignment-cities-${assignment.assignment_id}`}>
              {assignment.cities.map((c) => (
                <Badge key={c} className="bg-zinc-800 border border-zinc-700 text-zinc-300 text-[10px]">{c}</Badge>
              ))}
            </div>
          )}
        </div>
      </div>

      {assignment.notes && (
        <p className="text-[11px] text-zinc-400 italic mb-2">Note: {assignment.notes}</p>
      )}

      <ol className="space-y-1.5" data-testid={`crew-assignment-props-${assignment.assignment_id}`}>
        {visibleProps.map((p, idx) => (
          <li
            key={p.property_id}
            className="rounded-md border border-zinc-800 bg-zinc-900/60 p-2.5"
            data-testid={`crew-assignment-prop-${p.property_id}`}
          >
            <div className="flex items-center gap-2">
              <span className="h-6 w-6 rounded-full flex items-center justify-center text-[11px] font-bold bg-zinc-800 text-[#C9A962]">
                {idx + 1}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-bold text-white">{p.property_name}</span>
                  <span className="text-[10px] text-zinc-500">{p.city}</span>
                  <Badge className="bg-zinc-800 text-zinc-200 border border-zinc-700 text-[10px]">
                    {p.total_units} units{p.total_buildings ? ` · ${p.total_buildings} bldg` : ''}
                  </Badge>
                </div>
              </div>
              {canOrganize && (
                <div className="flex flex-col gap-0.5 shrink-0">
                  <button
                    type="button"
                    onClick={() => moveProp(p.property_id, -1)}
                    className="h-5 w-5 rounded bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center text-zinc-300"
                    disabled={idx === 0}
                    data-testid={`crew-assignment-up-${p.property_id}`}
                    title="Move up"
                  >
                    <ChevronUp className="h-3 w-3" />
                  </button>
                  <button
                    type="button"
                    onClick={() => moveProp(p.property_id, +1)}
                    className="h-5 w-5 rounded bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center text-zinc-300"
                    disabled={idx === visibleProps.length - 1}
                    data-testid={`crew-assignment-down-${p.property_id}`}
                    title="Move down"
                  >
                    <ChevronDown className="h-3 w-3" />
                  </button>
                </div>
              )}
            </div>

            {canOrganize && assignment.agents.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1 pl-8">
                {assignment.agents.map((a) => {
                  const assigned = (dispatch[p.property_id] || []).includes(a.user_id);
                  return (
                    <button
                      key={a.user_id}
                      type="button"
                      onClick={() => toggleAgentOnProp(p.property_id, a.user_id)}
                      className={`px-2 py-0.5 rounded-full text-[10px] font-bold border transition ${
                        assigned
                          ? 'bg-emerald-600 border-emerald-500 text-white'
                          : 'bg-zinc-900 border-zinc-700 text-zinc-400 hover:border-emerald-500'
                      }`}
                      data-testid={`crew-assignment-dispatch-${p.property_id}-${a.user_id}`}
                      title={assigned ? `${a.name} is dispatched here` : `Dispatch ${a.name} to this building`}
                    >
                      {a.name.split(' ')[0]}
                    </button>
                  );
                })}
              </div>
            )}

            {!canOrganize && (p.assigned_agent_ids?.length > 0) && (
              <div className="mt-1 pl-8 text-[10px] text-zinc-500">
                Team: {p.assigned_agent_ids
                  .map((aid) => assignment.agents.find((a) => a.user_id === aid)?.name)
                  .filter(Boolean)
                  .join(', ') || '—'}
              </div>
            )}
          </li>
        ))}
      </ol>

      {canOrganize && (
        <div className="mt-3 flex flex-wrap gap-2 justify-end">
          <Button
            variant="outline"
            onClick={() => save()}
            disabled={saving}
            className="border-zinc-600 text-zinc-200 hover:bg-zinc-800 text-xs h-8"
            data-testid={`crew-assignment-save-${assignment.assignment_id}`}
          >
            {saving ? 'Saving…' : 'Save Order + Dispatch'}
          </Button>
          {statusKey === 'dispatched' && (
            <Button
              onClick={() => save('in_progress')}
              disabled={saving}
              className="bg-amber-600 hover:bg-amber-500 text-black text-xs h-8"
              data-testid={`crew-assignment-start-${assignment.assignment_id}`}
            >
              Start Run
            </Button>
          )}
          {statusKey === 'in_progress' && (
            <Button
              onClick={() => save('completed')}
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs h-8"
              data-testid={`crew-assignment-complete-${assignment.assignment_id}`}
            >
              Mark Complete
            </Button>
          )}
        </div>
      )}
    </Card>
  );
}
